import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import camelCase from 'lodash/camelCase'
import startCase from 'lodash/startCase'
import { getObjectKeyFromValue } from '../../../utils/formatting'
import * as d3 from 'd3'

import { AppLayoutContext } from '../../../providers/AppLayout'
import { useUrlStateParams } from '../../../utils/url'
import { SnackbarContext } from '../../../providers/SnackbarContext'

import { Hospital, hospitalFields } from '../../../models/Hospital.model'
import { USStates } from '../../../models/USStates.model'
import { HospitalAddEvent } from '../../../events/HospitalAddEvent'
import { HospitalUpdateEvent } from '../../../events/HospitalUpdateEvent'

import { HospitalService } from '../../../services/HospitalService/HospitalService'

import {
  Box,
  FormControl,
  OutlinedInput,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SearchIcon from '@mui/icons-material/Search'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ClearIcon from '@mui/icons-material/Clear'
import DataTable from '../../../components/DataTable/DataTable.component'
import Loading from '../../../components/Loading/Loading.component'
import ImportExportDialog, {
  ImportExportDialogProps,
} from '../../../components/ImportExportDialog/ImportExportDialog.component'
import StarRating from '../../../components/StarRating/StarRating.component'
import Link from '@mui/material/Link'
import HospitalFormDialog from '../shared/HospitalFormDialog.component'
import Avatar from '@mui/material/Avatar'
import Tooltip from '@mui/material/Tooltip'

const HospitalSearch = (): JSX.Element => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [searchError, setSearchError] = useState('')
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<null | HTMLElement>(null)
  const isActionsMenuOpen = Boolean(rowActionsAnchorEl)
  const [selectedRow, setSelectedRow] = useState<Hospital | undefined>()
  const [isAdding, setIsAdding] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const [term, setTerm] = useUrlStateParams(
    '',
    'term',
    (value) => encodeURIComponent(value.toString()),
    (value) => (value ? decodeURIComponent(value) : ''),
  )
  const termInputRef = useRef<HTMLInputElement>()
  const [state, setState] = useUrlStateParams<USStates | undefined>(
    undefined,
    'state',
    (value) => (value ? encodeURIComponent(value.toString()) : ''),
    (value) => {
      // confirm the value provided is a valid state enum value
      const stateOptions = Object.values(USStates)
      return stateOptions.includes(value as USStates) ? (value as USStates) : undefined
    },
  )
  const [tempState, setTempState] = useState<USStates | undefined>(state)
  const [orderBy, setOrderBy] = useState<keyof Hospital | undefined>('name')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc' | undefined>('asc')
  const [page, setPage] = useUrlStateParams(
    1,
    'page',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 1),
  )
  const [pageSize, setPageSize] = useUrlStateParams(
    12,
    'page-size',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 12),
  )

  const [hospitals, setHospitals] = useState<Hospital[]>([])
  const [totalResults, setTotalResults] = useState(0)

  const [isImportExportOpen, setIsImportExportOpen] = useState(false)

  const hospitalService = useMemo(() => new HospitalService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName('Hospitals')
  }, [])

  useEffect(() => {
    setIsLoading(true)
    setSearchError('')
    hospitalService
      .search({ page, pageSize, term, state: state || undefined, orderBy, orderDirection })
      .then((results) => {
        setIsLoading(false)
        setHospitals(results.hospitals)
        setTotalResults(results.totalResults)
        setPage(results.currentPage)
      })
      .catch((err) => {
        setSearchError(err)
        console.error(err)
        setIsLoading(false)
      })
  }, [state, term, page, pageSize, orderBy, orderDirection])

  const handleRowActionsClick = (event: React.MouseEvent<HTMLButtonElement>, hospital: Hospital) => {
    setRowActionsAnchorEl(event.currentTarget)
    setSelectedRow(hospital)
  }
  const handleRowActionsClose = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
  }

  const columns = useMemo(() => {
    const columns: GridColDef<Hospital>[] = [
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButton
              id={`row-actions-button-${params.row.id}`}
              color="primary"
              aria-label="row actions"
              aria-controls={selectedRow?.id === params.row.id ? 'row-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={selectedRow?.id === params.row.id ? 'true' : undefined}
              onClick={(event) => handleRowActionsClick(event, params.row)}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        ),
      },
      {
        field: 'imageURL',
        headerName: 'Image',
        width: 100,
        sortable: false,
        renderCell: (params) =>
          params.row.imageURL ? (
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.50',
              }}
            >
              <img
                src={params.row.imageURL}
                alt={params.row.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 1,
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <LocalHospitalIcon sx={{ fontSize: 28, color: '#007C91', opacity: 0.4 }} />
            </Box>
          ),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 100,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: '#263238' }}>
            {params.row.id}
          </Typography>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 350,
        renderCell: (params) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#263238' }}>
              {params.row.name}
            </Typography>
            {params.row.address && (
              <Typography variant="caption" sx={{ display: 'block', color: '#007C91' }}>
                {params.row.address}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'city',
        headerName: 'City',
        width: 150,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: '#263238' }}>
            {params.row.city || '-'}
          </Typography>
        ),
      },
      {
        field: 'state',
        headerName: 'State',
        width: 100,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: '#263238' }}>
            {params.row.state || '-'}
          </Typography>
        ),
      },
      {
        field: 'zipcode',
        headerName: 'Zipcode',
        width: 120,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: '#263238' }}>
            {params.row.zipcode || '-'}
          </Typography>
        ),
      },
      {
        field: 'averageRating',
        headerName: 'Rating',
        width: 150,
        renderCell: (params) => (
          <Box>
            {params.row.averageRating !== undefined && params.row.averageRating !== null ? (
              <>
                <StarRating rating={Number(params.row.averageRating) || 0} size="small" />
                {params.row.reviewCount !== undefined && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#007C91' }}>
                    {params.row.reviewCount} {params.row.reviewCount === 1 ? 'review' : 'reviews'}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" sx={{ color: '#263238' }}>
                -
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'latitude',
        headerName: 'Coordinates',
        width: 200,
        renderCell: (params) => (
          <Box>
            {params.row.latitude !== null && params.row.longitude !== null ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <IconButton
                  component="a"
                  href={`https://www.google.com/maps?q=${params.row.latitude},${params.row.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{
                    color: 'primary.main',
                    padding: 0.5,
                    alignSelf: 'center',
                    '&:hover': {
                      bgcolor: 'rgba(0, 124, 145, 0.08)',
                    },
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <LocationOnIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Box>
                  <Typography variant="body2" sx={{ display: 'block', color: '#263238' }}>
                    Lat: {params.row.latitude}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#263238' }}>
                    Lng: {params.row.longitude}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#263238', marginLeft: 1 }}>
                -
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'supportTelephone',
        headerName: 'Phone',
        width: 150,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: '#263238' }}>
            {params.row.supportTelephone && params.row.supportTelephone !== 'NOT AVAILABLE'
              ? params.row.supportTelephone
              : '-'}
          </Typography>
        ),
      },
      {
        field: 'supportEmail',
        headerName: 'Email',
        width: 200,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: '#263238' }}>
            {params.row.supportEmail || '-'}
          </Typography>
        ),
      },
      {
        field: 'website',
        headerName: 'Website',
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Box>
            {params.row.website ? (
              <Tooltip title={params.row.website}>
                <IconButton
                  href={params.row.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Typography variant="body2" sx={{ color: '#263238', marginLeft: 1 }}>
                -
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'permalink',
        headerName: 'Permalink',
        width: 350,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%',
              color: '#263238',
            }}
          >
            {params.row.permalink || '-'}
          </Typography>
        ),
      },
      {
        field: 'externalId',
        headerName: 'External ID',
        width: 120,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: '#263238' }}>
            {params.row.externalId || '-'}
          </Typography>
        ),
      },
      {
        field: 'stableId',
        headerName: 'Stable ID',
        width: 120,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', color: '#263238' }}>
            {params.row.stableId || '-'}
          </Typography>
        ),
      },
      {
        field: 'facilityCode',
        headerName: 'Facility Code',
        width: 180,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: '#263238' }}>
            {params.row.facilityCode || '-'}
          </Typography>
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        width: 180,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: '#263238' }}>
            {params.row.createdAt ? new Date(params.row.createdAt).toLocaleString() : '-'}
          </Typography>
        ),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        width: 180,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: '#263238' }}>
            {params.row.updatedAt ? new Date(params.row.updatedAt).toLocaleString() : '-'}
          </Typography>
        ),
      },
    ]
    return columns
  }, [selectedRow])

  const handleSubmit = () => {
    setPage(1)
    setTerm(termInputRef?.current?.value || '')
    setState(tempState)
  }

  const handleAddClick = () => {
    setIsAdding(true)
  }

  const handleAddCancel = () => {
    setIsAdding(false)
  }

  const handleAddSubmit = (
    newHospital: HospitalAddEvent,
    setIsAddFormSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (!Object.keys(newHospital).length) {
      // nothing changed so just close the dialog
      setIsAdding(false)
    } else {
      setIsLoading(true)
      hospitalService
        .add(newHospital)
        .then(() => {
          SnackbarContext.show('Hospital added successfully!')

          setIsAdding(false)

          // fetch new data
          setSearchError('')
          hospitalService
            .search({ pageSize, term, state: state || undefined, orderBy, orderDirection })
            .then((results) => {
              setHospitals(results.hospitals)
              setTotalResults(results.totalResults)
              setPage(1)
              setIsLoading(false)
            })
            .catch((err) => {
              setSearchError(err)
              console.error(err)
              setIsLoading(false)
            })
        })
        .catch((err) => {
          SnackbarContext.show(`Hospital failed to add: ${err?.data?.message || err?.message || err}`, 'error')
          console.error(err)
          setIsLoading(false)
          setIsAddFormSubmitting(false)
        })
    }
  }

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleEditCancel = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
    setIsEditing(false)
  }

  const handleEditSubmit = (
    changedProperties: HospitalUpdateEvent,
    setIsEditFormSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (!Object.keys(changedProperties).length) {
      // nothing changed so just close the dialog
      setRowActionsAnchorEl(null)
      setSelectedRow(undefined)
      setIsEditing(false)
    } else if (selectedRow) {
      setIsLoading(true)
      hospitalService
        .update(Number(selectedRow.id), changedProperties)
        .then((result) => {
          SnackbarContext.show('Hospital updated successfully!')
          setRowActionsAnchorEl(null)
          setSelectedRow(undefined)
          // replace the hospital in the list with the updated one
          setHospitals((prevHospitals) => {
            const index = prevHospitals.findIndex((hospital) => hospital.id === result.id)
            if (index > -1) {
              const newHospitals = [...prevHospitals]
              newHospitals[index] = result
              return newHospitals
            }
            return prevHospitals
          })
          setIsLoading(false)
          setIsEditing(false)
        })
        .catch((err) => {
          SnackbarContext.show(`Hospital failed to edit: ${err?.data?.message || err?.message || err}`, 'error')
          console.error(err)
          setIsLoading(false)
          setIsEditFormSubmitting(false)
        })
    }
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false)
    handleRowActionsClose()
  }

  const handleDelete = () => {
    if (selectedRow?.id) {
      setIsLoading(true)
      setIsDeleteConfirmationOpen(false)
      hospitalService
        .delete(selectedRow.id)
        .then(() => {
          SnackbarContext.show('Hospital deleted successfully!')
          setRowActionsAnchorEl(null)
          setSelectedRow(undefined)
          setHospitals(hospitals.filter((hospital) => hospital.id !== selectedRow?.id))
          setTotalResults(totalResults - 1)
          setIsLoading(false)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Hospital failed to delete: ${err?.data?.message || err?.message || err}`, 'error')
          setIsLoading(false)
        })
    }
  }

  const openImportExport = () => {
    setIsImportExportOpen(true)
  }

  const handleImportExportClose = () => {
    setIsImportExportOpen(false)
  }

  const handleImport: ImportExportDialogProps['onImport'] = (data: Partial<Hospital>[], options) => {
    setIsImportExportOpen(false)
    if (!data) {
      SnackbarContext.show('Failed to read/parse file', 'error')
      console.error('Failed to read/parse file')
    } else {
      // Filter out fields that should not be imported (facilityCode, stableId, updatedAt, createdAt)
      const fieldsToExclude = ['facilityCode', 'stableId', 'updatedAt', 'createdAt']
      const filteredData = data.map((row) => {
        const filteredRow = { ...row }
        fieldsToExclude.forEach((field) => {
          delete filteredRow[field as keyof Hospital]
        })
        return filteredRow
      })

      setIsLoading(true)
      hospitalService
        .import(filteredData, options)
        .then(() => {
          SnackbarContext.show('Hospitals imported successfully!')

          // fetch new data
          setSearchError('')
          hospitalService
            .search({ pageSize, term, state: state || undefined, orderBy, orderDirection })
            .then((results) => {
              setHospitals(results.hospitals)
              setTotalResults(results.totalResults)
              setPage(1)
              setIsLoading(false)
            })
            .catch((err) => {
              setSearchError(err)
              console.error(err)
              setIsLoading(false)
            })
        })
        .catch((err) => {
          SnackbarContext.show(`Hospital failed to import: ${err?.data?.message || err?.message || err}`, 'error')
          console.error(err)
          setIsLoading(false)
        })
    }
  }

  const handleExport = () => {
    // do a request to the API to get the data to export with no pagination
    setIsLoading(true)
    setIsImportExportOpen(false)
    hospitalService
      .search({ page: 1, pageSize: 999999, term, state: state || undefined, orderBy, orderDirection })
      .then((results) => {
        if (!results.hospitals || results.hospitals.length === 0) {
          SnackbarContext.show('No hospitals found to export', 'warning')
          setIsLoading(false)
          return
        }

        const exportData = results.hospitals.map((hospital) => ({
          id: hospital.id,
          stableId: hospital.stableId || '',
          name: hospital.name,
          imageURL: hospital.imageURL || '',
          permalink: hospital.permalink,
          latitude: hospital.latitude !== null ? String(hospital.latitude) : '',
          longitude: hospital.longitude !== null ? String(hospital.longitude) : '',
          address: hospital.address || '',
          city: hospital.city || '',
          state: getObjectKeyFromValue(USStates, hospital.state) || '',
          zipcode: hospital.zipcode || '',
          website: hospital.website || '',
          supportTelephone: hospital.supportTelephone || '',
          supportEmail: hospital.supportEmail || '',
          externalId: hospital.externalId || '',
          facilityCode: hospital.facilityCode || '',
          averageRating: String(hospital.averageRating),
          reviewCount: String(hospital.reviewCount),
          updatedAt: hospital.updatedAt,
          createdAt: hospital.createdAt,
        }))

        if (exportData.length === 0) {
          SnackbarContext.show('No data to export', 'warning')
          setIsLoading(false)
          return
        }

        const csvData = d3.csvFormat(exportData)

        if (!csvData || csvData.trim().length === 0) {
          SnackbarContext.show('Failed to generate CSV data', 'error')
          setIsLoading(false)
          return
        }

        // initiate a download of the csv file
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.setAttribute('href', url)
        link.setAttribute('download', 'hospitals.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        SnackbarContext.show(`Exported ${exportData.length} hospitals successfully!`)
        setIsLoading(false)
      })
      .catch((err) => {
        SnackbarContext.show(
          `Hospital failed to fetch hospitals for export: ${err?.data?.message || err?.message || err}`,
          'error',
        )
        console.error(err)
        setIsLoading(false)
      })
  }

  return (
    <>
      {selectedRow && (
        <HospitalFormDialog
          open={isEditing}
          hospital={selectedRow}
          onEdit={handleEditSubmit}
          onCancel={handleEditCancel}
          onClose={handleEditCancel}
        />
      )}
      <HospitalFormDialog
        open={isAdding}
        onAdd={handleAddSubmit}
        onCancel={handleAddCancel}
        onClose={handleAddCancel}
      />
      <Dialog fullWidth maxWidth="xs" open={isDeleteConfirmationOpen} onClose={handleDeleteConfirmationClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          Are you sure you want to delete hospital id {selectedRow?.id}: {selectedRow?.name}?
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleDeleteConfirmationClose}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <ImportExportDialog
        open={isImportExportOpen}
        exampleImportFileUrl="/example-import-files/hospitals.csv"
        importFields={hospitalFields.filter(
          (field) => !['updatedAt', 'createdAt', 'stableId', 'facilityCode'].includes(field),
        )}
        onImport={handleImport}
        onExport={handleExport}
        onClose={handleImportExportClose}
      />
      <Paper sx={{ p: { xxxs: 1, xxs: 1.25, xs: 1.5, sm: 2 } }}>
        {isLoading && !searchError && <Loading sx={{ py: 20 }} />}
        {searchError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{searchError}</Typography>
          </Stack>
        )}
        {!isLoading && !searchError && (
          <>
            <Box sx={{ mb: { xxxs: 1, xxs: 1.25, xs: 1.5, sm: 2 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xxxs: 1, xxs: 1.25, xs: 1.5, sm: 2 }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{ width: '100%' }}
              >
                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    flex: { md: '1 1 250px' },
                    minWidth: { md: 200 },
                    maxWidth: { md: 'none' },
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xxxs: '0.875rem', xs: '0.9375rem' },
                      height: { xxxs: '36px', xs: '40px' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xxxs: '0.875rem', xs: '0.9375rem' },
                    },
                  }}
                >
                  <InputLabel>Hospital Name</InputLabel>
                  <OutlinedInput
                    id="search-term"
                    label="Hospital Name"
                    defaultValue={term || ''}
                    inputRef={termInputRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit()
                      }
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{
                            fontSize: { xxxs: '18px', xs: '20px' },
                          }}
                          color="action"
                        />
                      </InputAdornment>
                    }
                    endAdornment={
                      term ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear search term"
                            onClick={() => {
                              if (termInputRef?.current?.value) {
                                termInputRef.current.value = ''
                              }
                              setPage(1)
                              setTerm('')
                            }}
                            edge="end"
                            size="small"
                            sx={{
                              padding: { xxxs: '4px', xs: '8px' },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined
                    }
                  />
                </FormControl>

                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    flex: { md: '0 0 200px' },
                    minWidth: { md: 180 },
                    maxWidth: { md: 'none' },
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xxxs: '0.875rem', xs: '0.9375rem' },
                      height: { xxxs: '36px', xs: '40px' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xxxs: '0.875rem', xs: '0.9375rem' },
                    },
                  }}
                >
                  <InputLabel id="state-select-label">State</InputLabel>
                  <Select
                    labelId="state-select-label"
                    id="state-select"
                    value={tempState || ''}
                    label="State"
                    onChange={(e) => {
                      setTempState(e.target.value as USStates)
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <LocationOnIcon
                          sx={{
                            fontSize: { xxxs: '16px', xs: '18px' },
                          }}
                          color="action"
                        />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">
                      <em>All States</em>
                    </MenuItem>
                    {Object.entries(USStates).map((eachState) => {
                      return (
                        <MenuItem key={eachState[0]} value={eachState[1]}>
                          {startCase(camelCase(eachState[0]))}
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xxxs: 1, xxs: 1.25, xs: 1.5, sm: 1.5 }}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    flex: { md: '0 0 auto' },
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSubmit}
                    startIcon={<SearchIcon />}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { xs: 'auto', sm: 95 },
                      height: { xxxs: '36px', xs: '40px' },
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: { xxxs: '0.8125rem', xs: '0.875rem' },
                      px: { xxxs: 1.5, xs: 2 },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Search
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { xs: 'auto', sm: 110 },
                      height: { xxxs: '36px', xs: '40px' },
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: { xxxs: '0.8125rem', xs: '0.875rem' },
                      px: { xxxs: 1.5, xs: 2 },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Add
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ImportExportIcon />}
                    onClick={openImportExport}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { xs: 'auto', sm: 135 },
                      height: { xxxs: '36px', xs: '40px' },
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: { xxxs: '0.75rem', xxs: '0.8125rem', xs: '0.875rem' },
                      px: { xxxs: 1.5, xxs: 1.5, xs: 2 },
                      whiteSpace: 'nowrap',
                      '& .MuiButton-startIcon': {
                        marginRight: { xxxs: 0.5, xs: 1 },
                      },
                    }}
                  >
                    Import/Export
                  </Button>
                </Stack>
              </Stack>

              {/* Active Filters Display */}
              {(term || state) && (
                <Box sx={{ mt: { xxxs: 1, xxs: 1.25, xs: 1.5, sm: 2 } }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ gap: { xxxs: 0.5, xxs: 0.75, xs: 0.75, sm: 1 } }}
                  >
                    {term && (
                      <Chip
                        label={
                          <Box
                            component="span"
                            sx={{
                              maxWidth: { xxxs: '120px', xxs: '150px', xs: '200px', sm: 'none' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {`Name: ${term}`}
                          </Box>
                        }
                        onDelete={() => {
                          if (termInputRef?.current?.value) {
                            termInputRef.current.value = ''
                          }
                          setPage(1)
                          setTerm('')
                        }}
                        color="primary"
                        sx={{
                          fontWeight: 500,
                          fontSize: { xxxs: '0.6875rem', xxs: '0.75rem', xs: '0.75rem', sm: '0.8125rem' },
                          height: { xxxs: 24, xxs: 26, xs: 28, sm: 32 },
                          '& .MuiChip-label': {
                            px: { xxxs: 0.75, xxs: 1, xs: 1, sm: 1.25 },
                          },
                        }}
                      />
                    )}
                    {state && (
                      <Chip
                        label={`State: ${startCase(camelCase(getObjectKeyFromValue(USStates, state) || ''))}`}
                        onDelete={() => {
                          setTempState(undefined)
                          setPage(1)
                          setState(undefined)
                        }}
                        color="secondary"
                        sx={{
                          fontWeight: 500,
                          fontSize: { xxxs: '0.6875rem', xxs: '0.75rem', xs: '0.75rem', sm: '0.8125rem' },
                          height: { xxxs: 24, xxs: 26, xs: 28, sm: 32 },
                          '& .MuiChip-label': {
                            px: { xxxs: 0.75, xxs: 1, xs: 1, sm: 1.25 },
                          },
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              )}
            </Box>

            {/* Results Section */}
            {hospitals.length > 0 ? (
              <>
                <Menu
                  id="row-actions-menu"
                  anchorEl={rowActionsAnchorEl}
                  open={isActionsMenuOpen}
                  onClose={handleRowActionsClose}
                  MenuListProps={{
                    'aria-labelledby': `row-actions-button${selectedRow?.id}`,
                  }}
                  PaperProps={{
                    sx: {
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                      mt: 1,
                    },
                  }}
                >
                  <MenuItem href={`/hospitals/${selectedRow?.id}`} component={Link}>
                    View
                  </MenuItem>
                  <MenuItem onClick={handleEditClick}>Edit</MenuItem>
                  <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
                </Menu>
                <DataTable
                  columns={columns}
                  data={hospitals}
                  total={totalResults}
                  paginationMode="server"
                  page={page}
                  pageSize={pageSize}
                  onPageChange={(newPage) => {
                    setPage(newPage)
                  }}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize)
                  }}
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  onOrderChange={(newOrder) => {
                    setOrderBy(newOrder.field ? (newOrder.field as keyof Hospital) : undefined)
                    setOrderDirection(newOrder.sort)
                  }}
                  onRowClick={(row) => {
                    navigate(`/hospitals/${row.id}`)
                  }}
                />
              </>
            ) : (
              <Box
                sx={{
                  p: { xxxs: 2, xxs: 3, xs: 4, sm: 6, md: 8 },
                  textAlign: 'center',
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: '2px dashed',
                  borderColor: 'divider',
                }}
              >
                <LocalHospitalIcon
                  sx={{
                    fontSize: { xxxs: 40, xxs: 44, xs: 48, sm: 56, md: 64 },
                    color: 'text.secondary',
                    mb: { xxxs: 1, xxs: 1.25, xs: 1.5, sm: 2 },
                    opacity: 0.5,
                  }}
                />
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{
                    mb: { xxxs: 0.75, xs: 1 },
                    fontWeight: 600,
                    fontSize: { xxxs: '0.9375rem', xxs: '1rem', xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  No hospitals found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xxxs: '0.8125rem', xxs: '0.875rem', xs: '0.875rem', sm: '0.9375rem' },
                    px: { xxxs: 1, xxs: 1.5, xs: 2, sm: 0 },
                  }}
                >
                  {term || state
                    ? 'Try adjusting your search filters to find more results.'
                    : 'Get started by adding a new hospital or importing from a file.'}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default HospitalSearch
